import { PageHero } from '@components/common';
import { MapPin, Clock, CheckCircle2, Briefcase } from 'lucide-react';

export default function ClientSuccessManagerPage() {
  return (
    <>
      <PageHero
        title="Client Success Manager"
        eyebrow="Careers"
        seoTitle="Client Success Manager | The CISO Network"
        seoDescription="Join us as a Client Success Manager. Ensure our clients get maximum value from their fractional CISO engagements."
      />

      {/* Role Overview */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4 mb-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Remote UK
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Full-time
            </span>
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4" />
              4+ years experience
            </span>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                About the Role
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-muted-foreground mb-4">
                  As our Client Success Manager, you'll be the bridge between our fractional CISOs
                  and the businesses they serve. Your mission is simple but critical: ensure every
                  client gets exceptional value from their engagement with The CISO Network.
                </p>
                <p className="text-muted-foreground mb-4">
                  You'll oversee the entire client journey - from onboarding through ongoing
                  engagement - proactively identifying opportunities to enhance value and addressing
                  any issues before they become problems.
                </p>
                <p className="text-muted-foreground mb-8">
                  This is a relationship-focused role that requires a unique blend of customer
                  empathy, project management, and understanding of cybersecurity services.
                </p>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  Key Responsibilities
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    'Own the end-to-end client relationship, from onboarding through renewal',
                    'Conduct regular check-ins with clients to assess satisfaction and value realisation',
                    'Identify and escalate at-risk accounts, developing retention strategies',
                    'Coordinate between clients and fractional CISOs to ensure smooth service delivery',
                    'Gather and analyse client feedback to inform service improvements',
                    'Support contract renewals and expansion discussions',
                    'Develop and maintain client success metrics and reporting',
                    'Create and refine client onboarding processes and materials',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  What We're Looking For
                </h3>
                <ul className="space-y-3 mb-8">
                  {[
                    '4+ years of experience in customer success, account management, or consulting',
                    'Track record of managing B2B client relationships, ideally in professional services',
                    'Strong organisational skills with ability to manage multiple client engagements',
                    'Excellent communication and interpersonal skills',
                    'Understanding of cybersecurity or technology services (technical depth not required)',
                    'Data-driven approach with experience in metrics and reporting',
                    'Proactive problem-solver who anticipates client needs',
                    'Experience with CRM systems (HubSpot, Salesforce, or similar)',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-heading font-bold text-foreground mb-4">Why Join Us</h3>
                <p className="text-muted-foreground mb-4">
                  You'll play a critical role in a growing company, helping shape how we deliver
                  value to our clients. This is an opportunity to build something meaningful while
                  developing deep expertise in the cybersecurity industry.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Competitive salary: £45,000-£65,000 depending on experience',
                    'Remote-first culture with flexible working arrangements',
                    '£2,000 annual professional development budget',
                    'Opportunity to shape client success function from the ground up',
                    'Work with a diverse portfolio of interesting clients',
                    'Clear progression path to Head of Client Success',
                    'Regular exposure to C-level executives across multiple industries',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-teal mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">
                  Apply for this Role
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This position is currently closed. We are not accepting applications at this time,
                  but please check back in the future.
                </p>
                <button
                  disabled
                  className="w-full px-6 py-3 rounded-lg bg-muted text-muted-foreground font-medium cursor-not-allowed"
                >
                  Position Closed
                </button>
              </div>

              <div className="p-6 rounded-xl bg-background border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-4">Questions?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Interested to learn more about the role before applying? We're happy to have an
                  informal chat.
                </p>
                <a
                  href="mailto:careers@thecisonetwork.com?subject=Question: Client Success Manager Role"
                  className="text-teal hover:underline text-sm"
                >
                  Contact our recruitment team
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
